// Filter tags component for displaying selected filters

import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { FilterTag } from '@/types/filters';

interface FilterTagsProps {
  tags: FilterTag[];
  onRemoveTag: (tagId: string) => void;
  onClearAll?: () => void;
  showClearAll?: boolean;
  showHeader?: boolean;
  headerText?: string;
}

export const FilterTags: React.FC<FilterTagsProps> = ({
  tags,
  onRemoveTag,
  onClearAll,
  showClearAll = false,
  showHeader = true,
  headerText = "Applied Filters"
}) => {
  const getTagColorClasses = (color: string) => {
    // If color already contains class names, return it directly
    if (color.includes('bg-') || color.includes('text-')) {
      return color;
    }

    // Otherwise use the color map
    const colorMap = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200',
    };

    return colorMap[color as keyof typeof colorMap] || colorMap.gray;
  };

  const MAX_TAGS_TO_DISPLAY = 6;
  const [displayCondensedTags, setDisplayCondensedTags] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);

  useEffect(() => {
    const shouldDisplayCondensedTags = (): boolean => {
      return tags.length > 0 && tags.length > MAX_TAGS_TO_DISPLAY;
    };
    setDisplayCondensedTags(shouldDisplayCondensedTags());
  }, [tags.length])

  const classStringForSection = (): string => {
    let classString = "flex flex-wrap gap-2"
    if (showAllTags && displayCondensedTags) {
      classString = `${classString} h-52 overflow-y-auto`;
    }
    return classString;
  }

  if (tags.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No filters applied
      </div>
    );
  }

  return (
    <div className="space-y-3 border-b p-2">
      {showHeader && !displayCondensedTags && (
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900">
            {headerText} ({tags.length})
          </h4>
        </div>
      )}
      {showHeader && displayCondensedTags && (
        <div className="flex items-center justify-between">
          <button onClick={() => setShowAllTags(!showAllTags)} className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
            <h4 className="text-sm font-medium text-gray-900">{headerText} ({tags.length})</h4>
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${showAllTags ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}
      {showClearAll && onClearAll && (
        <button onClick={onClearAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
          Clear All
        </button>
      )}

      <div className={classStringForSection()}>

        {tags.map((tag) => (
          <div
            key={tag.id}
            className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getTagColorClasses(tag.color)}`}
          >
            <span className="font-semibold mr-1">{tag.label}:</span>
            <span className="truncate max-w-32" title={String(tag.value)}>
              {String(tag.value)}
            </span>
            <button
              onClick={() => onRemoveTag(tag.id)}
              className="ml-1.5 hover:bg-black hover:bg-opacity-10 rounded-full p-0.5 transition-colors"
              title={`Remove ${tag.label}: ${String(tag.value)}`}
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
